/// <amd-module name="Repos/Views/Settings/Repositories-Policies/Components/AutomaticReviewerPolicies" />
import * as React from "react";
import { IAutomaticReviewerPolicy } from "Repos/Views/Settings/Repositories-Policies/Contracts/Contracts";
import * as Resources from "Repos/Views/Settings/Repositories-Policies/Resources";
import { IObservableArray, IReadonlyObservableArray, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { PersonaDetailsProvider } from "VSS/Features/Identities/PersonaDetailsProvider";
import { showMessageDialog } from "VSS/Features/PlatformUI/MessageDialog";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Filter, getKeywordFilterItem } from "VSSUI/Filter";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IMenuItem, IMenuProps } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { Pill, PillSize, PillVariant } from "VSSUI/Pill";
import { ColumnMore, ColumnSorting, ITableColumn, ITableRow, SimpleTableCell, sortItems, SortOrder, Table, TableColumnLayout } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { Filter as FilterStore, FILTER_APPLIED_EVENT, IFilter } from "VSSUI/Utilities/Filter";
import { IPoliciesViewService, policyTypes } from "../Services/PoliciesViewService";
import { enabledColumn, inheritanceColumn, IPolicyColumn, pathColumn } from "./Columns";
import { PolicyCard } from "./PolicyCard";

interface IAutomaticReviewerProps {
    isEditable: boolean;
    ready: boolean;
    policies: IReadonlyObservableArray<IAutomaticReviewerPolicy>;
}

const loadingItems = new ObservableArray<IAutomaticReviewerPolicy>(
    new Array(3).fill(new ObservableValue<IAutomaticReviewerPolicy | undefined>(undefined))
);

export class AutomaticReviewerPolicies extends VssComponent<IAutomaticReviewerProps> {
    private columns: IPolicyColumn<IAutomaticReviewerPolicy>[];
    private filter: IFilter;
    private filteredItems = new ObservableArray<IAutomaticReviewerPolicy>();
    private personaProvider: PersonaDetailsProvider;
    private policyService: IPoliciesViewService;
    private sortColumnIndex: number = 1;
    private sortFunctions: ((a: IAutomaticReviewerPolicy, b: IAutomaticReviewerPolicy) => number)[];
    private sortOrder: SortOrder = SortOrder.ascending;

    private sortingBehavior = new ColumnSorting<IAutomaticReviewerPolicy>((columnIndex, order) => {
        this.sortColumnIndex = columnIndex;
        this.sortOrder = order;
        this.refreshItems();
    });

    constructor(props: IAutomaticReviewerProps, context: IVssComponentContext) {
        super(props, context);

        this.filter = new FilterStore();

        this.policyService = context.pageContext.getService<IPoliciesViewService>("PoliciesViewService");
        this.personaProvider = new PersonaDetailsProvider(this.context.pageContext);

        this.columns = this.getColumns();
        this.sortFunctions = this.columns.map(c => c.sortFunction!);
        this.refreshItems();

        this.observe([{ propertyName: "policies", observer: this.refreshItems }]);
    }

    public componentDidMount = () => {
        this.filter.subscribe(this.refreshItems, FILTER_APPLIED_EVENT);
    };

    public componentWillUnmount() {
        this.filter.unsubscribe(this.refreshItems);
    }

    public render = (): JSX.Element => {
        const { ready } = this.props;
        return (
            <Observer items={this.filteredItems}>
                {() => {
                    const itemCount = this.filteredItems.length;
                    return (
                        <PolicyCard
                            className="flex-grow policies"
                            filter={<Filter filterStore={this.filter} filterItems={[getKeywordFilterItem(this.filter, 400)]} items={[]} />}
                            headerCommandBarItems={this.getCommandBarItems()}
                            headerDescriptionText={Resources.AutomaticallyIncludedReviewersDescription}
                            itemCount={itemCount}
                            policyName="reviewers"
                            titleText={Resources.AutomaticallyIncludedReviewers}
                        >
                            <div className="settings-policies-wrapper">
                                {!ready || itemCount ? (
                                    <Table<IAutomaticReviewerPolicy>
                                        behaviors={[this.sortingBehavior]}
                                        itemProvider={ready ? this.filteredItems : loadingItems}
                                        columns={this.columns}
                                        onActivate={(event, row) => {
                                            if (!event.defaultPrevented) {
                                                this.onEdit(row);
                                            }
                                        }}
                                    />
                                ) : (
                                    <div className="flex-grow secondary-text padding-horizontal-20">{Resources.ZeroReviewer}</div>
                                )}
                            </div>
                        </PolicyCard>
                    );
                }}
            </Observer>
        );
    };

    private getColumns = (): IPolicyColumn<IAutomaticReviewerPolicy>[] => {
        return [
            enabledColumn,
            {
                columnLayout: TableColumnLayout.twoLine,
                id: "name",
                name: Resources.Reviewers,
                readonly: true,
                renderCell: (
                    rowIndex: number,
                    columnIndex: number,
                    tableColumn: ITableColumn<IAutomaticReviewerPolicy>,
                    tableItem: IAutomaticReviewerPolicy
                ): JSX.Element => {
                    const settings = tableItem.complexSettings!;
                    const rowTitle = getRowTitle(tableItem);

                    return (
                        <SimpleTableCell columnIndex={columnIndex} key={tableItem.id + "name"} tableColumn={tableColumn}>
                            <div className="flex-column margin-right-8">
                                {settings.reviewerIds.length === 1 && settings.reviewers.length === 1 ? (
                                    <Persona identity={settings.reviewers[0]} personaProvider={this.personaProvider} size={PersonaSize.size24} />
                                ) : (
                                    <Pill size={PillSize.regular} variant={PillVariant.standard}>
                                        {settings.reviewerIds.length}
                                    </Pill>
                                )}
                            </div>
                            <div className="flex-column text-ellipsis">
                                <div className="text-ellipsis">
                                    <Tooltip text={rowTitle}>
                                        <span className="body-m font-weight-semibold">{rowTitle}</span>
                                    </Tooltip>
                                </div>
                                <div className="text-ellipsis">
                                    <span className="secondary-text body-s">{settings.required ? Resources.Required : Resources.Optional}</span>
                                </div>
                            </div>
                        </SimpleTableCell>
                    );
                },
                sortFunction: (a, b) => localeIgnoreCaseComparer(getRowTitle(a), getRowTitle(b)),
                sortProps: { ariaLabelAscending: Resources.SortByReviewersAsc, ariaLabelDescending: Resources.SortByReviewersDesc },
                width: -45
            },
            pathColumn,
            inheritanceColumn,
            new ColumnMore(this.getMoreMenuItems, this.showMoreMenu)
        ];
    };

    private getCommandBarItems = (): IHeaderCommandBarItem[] => {
        return [
            {
                ariaLabel: Resources.AddNewReviewerPolicy,
                iconProps: { iconName: "Add" },
                id: "add-auto-reviewer-policy",
                disabled: !this.props.isEditable,
                onActivate: () => {
                    const newPolicy = this.policyService.getNewPolicy<IAutomaticReviewerPolicy>(policyTypes.requiredReviewers);
                    this.policyService.showEditPanel(newPolicy);
                }
            }
        ];
    };

    private getMoreMenuItems = (item: IAutomaticReviewerPolicy): IMenuProps => {
        const itemArr: IObservableArray<IMenuItem> = new ObservableArray([
            {
                id: "edit-view",
                text: Resources.EditView,
                data: item,
                onActivate: this.onEdit,
                iconProps: {
                    iconName: "View"
                }
            }
        ]);

        if (!item.isInherited && !item.isEnterpriseManaged) {
            itemArr.push({
                id: "delete",
                text: Resources.Delete,
                data: item,
                onActivate: this.onDelete,
                iconProps: {
                    iconName: "Delete"
                }
            });
        }

        const menuProps: IMenuProps = {
            id: "sub-menu",
            items: itemArr
        };

        return menuProps;
    };

    private onDelete = (menuItem: IMenuItem) => {
        const policy: IAutomaticReviewerPolicy = menuItem.data;
        showMessageDialog(this.context.pageContext, {
            lightDismiss: true,
            message: Resources.ConfirmDeletionMessage,
            okButtonProps: { danger: true, primary: false, text: Resources.Delete },
            title: Resources.ConfirmDeletion,
            onClose: result => {
                if (result) {
                    this.policyService.deleteReviewerPolicy(policy);
                }
            }
        });
    };

    private onEdit = (item: IMenuItem | ITableRow<IAutomaticReviewerPolicy>) => {
        this.policyService.showEditPanel(item.data);
    };

    private refreshItems = () => {
        const filterItemState = this.filter.getState()["keyword"];
        const filter = filterItemState && filterItemState.value ? filterItemState.value.toLocaleLowerCase() : "";

        let items = [...this.props.policies.value];
        if (filter) {
            items = items.filter(pol => {
                const { message, filenamePatterns, reviewers } = pol.complexSettings!;
                const texts = [message, filenamePatterns, ...reviewers.map(r => r.displayName)];
                return texts.some(text => text && text.toLowerCase().indexOf(filter) >= 0);
            });
        }

        this.filteredItems.value = sortItems(this.sortColumnIndex, this.sortOrder, this.sortFunctions, this.columns, items);
    };

    private showMoreMenu = (item: IAutomaticReviewerPolicy): boolean => {
        return !item.isInherited;
    };
}

function getRowTitle(policy: IAutomaticReviewerPolicy): string {
    const settings = policy.complexSettings!;
    return settings.message || settings.reviewers.length === 0
        ? settings.message
        : settings.reviewers.length === 1
        ? settings.reviewers[0].displayName!
        : format(Resources.PlusNOthers, settings.reviewers[0].displayName!, settings.reviewers.length - 1);
}
